import { useState, useEffect, useRef } from "react";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@b2b/ui-components";
import type { ProfileData, ProfileField } from "../types/profile";
import { ProfileFieldRenderer } from "./ProfileFieldRender";
import { DragDropZone } from "./BrandingMediaSection";
import { adminService, membershipApi } from "@b2b/services";
import { ChevronsUpDown, X } from "lucide-react";
import { SELLER_BUSINESS_TYPE_OPTIONS } from "../../../constants/sellerBusinessTypes";
import {
  extractRootIndustryOptions,
  resolveIndustryDisplayName,
} from "../../../utils/industryCategoryUtils";

const EMPLOYEE_OPTIONS = [
  { label: "1-10", value: "1-10" },
  { label: "11-50", value: "11-50" },
  { label: "51-200", value: "51-200" },
  { label: "201-500", value: "201-500" },
  { label: "501+", value: "501+" },
];

function getStr(f: ProfileField | undefined): string {
  if (!f || f.value == null) return "";
  return typeof f.value === "string" ? f.value : String(f.value);
}

/** Returns a user-facing error when the stored year is after the current calendar year. */
export function getFoundedYearFutureError(storedYearStr: string): string | undefined {
  const raw = storedYearStr.trim();
  if (raw === "" || raw === "0") return undefined;
  const y = parseInt(raw, 10);
  if (!Number.isFinite(y)) return undefined;
  const currentYear = new Date().getFullYear();
  if (y > currentYear) {
    return `Year cannot be later than ${currentYear}.`;
  }
  return undefined;
}

function sanitizeAlphaNumeric(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, "");
}

function sanitizeDigitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

function getIndustryTypes(ci: Record<string, ProfileField>): string[] {
  const f = ci.industryTypes;
  if (!f || !Array.isArray(f.value)) return [];
  return f.value as string[];
}

export type BusinessSubSection =
  | "business-details"
  | "contact-information"
  | "company-registration"
  | "about-company";

interface BusinessInformationSectionProps {
  subSection: BusinessSubSection;
  formData: ProfileData;
  isEditing: boolean;
  onFieldChange: (
    fieldKey: string,
    value: string | File | boolean | string[],
  ) => void;
  errors?: Record<string, string>;
  /** When provided, document uploads call this to get a URL before storing (e.g. profileService.uploadFile). */
  onUpload?: (file: File) => Promise<string>;
}

type CompanyRegDocKey = "gstDocumentUrl" | "panDocumentUrl" | "iecDocumentUrl";

/** Seller company-registration docs: same drag/drop behavior as Branding & Media logo (ProfileFieldRenderer upload has no drop handlers). */
function CompanyRegistrationDocUpload({
  readOnlyField,
  fieldKey,
  valueUrl,
  isEditing,
  onFieldChange,
  onUpload,
  maxFileSizeBytes,
  error,
}: {
  readOnlyField: ProfileField;
  fieldKey: CompanyRegDocKey;
  valueUrl: string;
  isEditing: boolean;
  onFieldChange: BusinessInformationSectionProps["onFieldChange"];
  onUpload?: (file: File) => Promise<string>;
  maxFileSizeBytes: number;
  error?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  if (!isEditing) {
    return (
      <ProfileFieldRenderer
        field={readOnlyField}
        fieldKey={fieldKey}
        isEditing={false}
        onValueChange={onFieldChange}
        error={error}
        hideRequiredAsteriskUnlessEditing
      />
    );
  }

  const handleFile = async (file: File | File[]) => {
    const f = Array.isArray(file) ? file[0] : file;
    if (!f) return;
    setUploadErr(null);
    if (f.size > maxFileSizeBytes) {
      setUploadErr("File size must be 2MB or less.");
      return;
    }
    const isPdf =
      f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    const isImage = f.type.startsWith("image/");
    if (!isPdf && !isImage) {
      setUploadErr("Please upload an image or PDF file.");
      return;
    }
    if (onUpload) {
      setUploading(true);
      try {
        const url = await onUpload(f);
        if (!url) {
          setUploadErr("Upload failed. No file URL returned.");
          return;
        }
        onFieldChange(fieldKey, url);
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Upload failed. Please try again.";
        setUploadErr(message);
      } finally {
        setUploading(false);
      }
    } else {
      onFieldChange(fieldKey, f);
    }
  };

  const maxMb = maxFileSizeBytes / (1024 * 1024);

  return (
    <div className="space-y-1">
      {/* <DragDropZone
        hint="Drag & drop or choose (PNG, JPG, PDF)"
        accept="image/*,.pdf"
        maxSizeMB={maxMb}
        value={valueUrl}
        onFile={handleFile}
        onClear={() => onFieldChange(fieldKey, "")}
        isEditing={isEditing}
        uploading={uploading}
        uploadError={uploadErr}
        className="py-5"
      /> */}
      <div className="space-y-2">
  <DragDropZone
    hint="Drag & drop or choose (PNG, JPG, PDF)"
    accept="image/*,.pdf"
    maxSizeMB={maxMb}
    value={valueUrl}
    onFile={handleFile}
    onClear={() => onFieldChange(fieldKey, "")}
    isEditing={isEditing}
    uploading={uploading}
    uploadError={uploadErr}
    className="py-5"
  />

  {/* ✅ NEW: Preview button aligned with Clear
  {valueUrl && (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => window.open(valueUrl, "_blank")}
        className="text-sm px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 transition"
      >
        Preview
      </button>
    </div>
  )} */}
</div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function BusinessInformationSection({
  subSection,
  formData,
  isEditing,
  onFieldChange,
  errors = {},
  onUpload,
}: BusinessInformationSectionProps) {
  const ci = formData.companyInfo || {};
  const [industryOptions, setIndustryOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [categoryTreeForLabels, setCategoryTreeForLabels] = useState<
    Array<{ id: string; name: string; parentId?: string; children?: unknown[] }>
  >([]);
  const [companyPhoneTooManyDigits, setCompanyPhoneTooManyDigits] = useState(false);

  const [maxIndustrySelections, setMaxIndustrySelections] = useState<number>(1);

  useEffect(() => {
    let cancelled = false;
    adminService.getCategories(true, false, 2).then((data) => {
      if (cancelled || !Array.isArray(data)) return;
      setCategoryTreeForLabels(data);
      setIndustryOptions(extractRootIndustryOptions(data));
    }).catch(() => {
      if (!cancelled) {
        setIndustryOptions([]);
        setCategoryTreeForLabels([]);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Fetch membership-based limits so the UI shows correct max industries.
  useEffect(() => {
    let cancelled = false;
    const loadMembershipLimit = async () => {
      try {
        const subscription = await membershipApi.getCompanySubscription();
        const plan = subscription?.plan;
        const features = plan?.features;
        const derived =
          Array.isArray(features) && features.length > 0
            ? (() => {
                for (const f of features) {
                  if (typeof f !== "string") continue;
                  const match = f.match(/(\d+)\s*Industry Selections/i);
                  if (match) return Number(match[1]);
                }
                return undefined;
              })()
            : undefined;

        if (!cancelled && typeof derived === "number" && Number.isFinite(derived)) {
          setMaxIndustrySelections(Math.max(1, derived));
        }
      } catch {
        // Fallback: keep default maxIndustrySelections.
      }
    };

    // Only needed for the business-details section (where industries are shown).
    if (subSection === "business-details") loadMembershipLimit();
    return () => {
      cancelled = true;
    };
  }, [subSection]);

  const companyPhoneStr = getStr(ci.companyPhone);
  useEffect(() => {
    if (subSection !== "contact-information" || !isEditing) {
      setCompanyPhoneTooManyDigits(false);
      return;
    }
    const digits = companyPhoneStr.replace(/\D/g, "");
    if (digits.length > 10) {
      setCompanyPhoneTooManyDigits(true);
      onFieldChange("companyPhone", digits.slice(0, 10));
    }
  }, [subSection, isEditing, companyPhoneStr, onFieldChange]);

  const industryTypesList = getIndustryTypes(ci);
  const industryTypesListKey = industryTypesList.slice().sort().join(",");
  const [savedIndustryTypesSnapshot, setSavedIndustryTypesSnapshot] = useState<string[]>(
    () => (subSection === "business-details" ? industryTypesList : [])
  );
  // Track the previous edit-mode to identify the transition from view -> edit.
  // Initialize to false so we correctly snapshot industries even if we mount while already editing.
  const prevIsEditingRef = useRef<boolean>(false);

  // Snapshot industries when entering edit mode so we can distinguish:
  // - Saved industries (non-removable)
  // - Newly added industries in this edit session (removable)
  useEffect(() => {
    if (subSection !== "business-details") return;
    if (!isEditing) {
      // When not editing, always treat the currently-loaded industries as saved.
      setSavedIndustryTypesSnapshot(industryTypesList);
    }
  }, [subSection, isEditing, industryTypesListKey]);

  useEffect(() => {
    if (subSection !== "business-details") return;
    if (isEditing && !prevIsEditingRef.current) {
      setSavedIndustryTypesSnapshot(industryTypesList);
    }
    prevIsEditingRef.current = isEditing;
  }, [subSection, isEditing, industryTypesListKey]);

  const savedIndustryTypesSet = new Set(savedIndustryTypesSnapshot);
  const atLimit = industryTypesList.length >= maxIndustrySelections;
  const industryIdToName = (idOrName: string) =>
    resolveIndustryDisplayName(
      idOrName,
      industryOptions,
      categoryTreeForLabels as Parameters<typeof resolveIndustryDisplayName>[2],
    );
  const [industryPopoverOpen, setIndustryPopoverOpen] = useState(false);
  const [foundedYearFieldError, setFoundedYearFieldError] = useState<string | undefined>();

  useEffect(() => {
    if (subSection !== "business-details") {
      setFoundedYearFieldError(undefined);
      return;
    }
    const s = getStr(ci.foundedYear).trim();
    const digits = s === "" || s === "0" ? "" : s.replace(/\D/g, "");
    setFoundedYearFieldError(getFoundedYearFutureError(digits));
  }, [subSection, ci.foundedYear?.value]);

  if (subSection === "business-details") {
    const rawEmployeeCount = getStr(ci.employeeCount);
    const parsed = rawEmployeeCount ? parseInt(rawEmployeeCount, 10) : NaN;
    const employeeBucket =
      Number.isFinite(parsed) && parsed >= 0
        ? parsed <= 10
          ? "1-10"
          : parsed <= 50
            ? "11-50"
            : parsed <= 200
              ? "51-200"
              : parsed <= 500
                ? "201-500"
                : "501+"
        : "";

    const companyBusinessTypeStr = getStr(
      ci.companyBusinessType ?? ci.businessType,
    ).trim();
    const sellerTypeOptions = SELLER_BUSINESS_TYPE_OPTIONS as readonly string[];
    const businessTypeSelectValue = sellerTypeOptions.includes(companyBusinessTypeStr)
      ? companyBusinessTypeStr
      : "";

    const foundedYearRaw = getStr(ci.foundedYear).trim();
    const foundedYearDisplay =
      foundedYearRaw === "" || foundedYearRaw === "0"
        ? ""
        : foundedYearRaw.replace(/\D/g, "");
    const foundedYearError = errors.foundedYear ?? foundedYearFieldError;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">
            Business Name{" "}
            {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <Input
            placeholder="Enter Business Name"
            value={getStr(ci.businessName)}
            onChange={(e) => onFieldChange("businessName", e.target.value)}
            disabled={!isEditing}
            className="h-10 border-gray-300 rounded"
          />
          <p className="text-xs text-muted-foreground">
            The operating name of your company, if it&apos;s different than the
            legal name.
          </p>
        </div>
        {ci.legalOwnerName && (
          <div className="space-y-1.5">
            <ProfileFieldRenderer
              field={ci.legalOwnerName}
              fieldKey="legalOwnerName"
              isEditing={isEditing}
              onValueChange={onFieldChange}
              error={errors.legalOwnerName}
              hideRequiredAsteriskUnlessEditing
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">
            Business Type{" "}
            {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <Select
            value={businessTypeSelectValue}
            onValueChange={(v) => onFieldChange("companyBusinessType", v)}
            disabled={!isEditing}
          >
            <SelectTrigger className="h-10 border-gray-300 rounded bg-white w-full">
              <SelectValue placeholder="Select Business Type" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 shadow-md z-50 ">
              {SELLER_BUSINESS_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">
            No. of Employees
          </Label>
          <Select
            value={employeeBucket}
            onValueChange={(v) => onFieldChange("employeeCount", v)}
            disabled={!isEditing}
          >
            <SelectTrigger className="h-10 border-gray-300 rounded bg-white w-full">
              <SelectValue placeholder="Select no. of employees" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 shadow-md z-50">
              {EMPLOYEE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-business-founded-year" className="text-sm font-medium text-gray-700">
            Year of Establishment
          </Label>
          <Input
            id="profile-business-founded-year"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Enter Year of Establishment"
            value={foundedYearDisplay}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "");
              onFieldChange("foundedYear", digits);
              setFoundedYearFieldError(getFoundedYearFutureError(digits));
            }}
            onBlur={() => {
              const s = getStr(ci.foundedYear).trim();
              const digits = s === "" || s === "0" ? "" : s.replace(/\D/g, "");
              if (digits !== s) {
                onFieldChange("foundedYear", digits);
              }
              setFoundedYearFieldError(getFoundedYearFutureError(digits));
            }}
            disabled={!isEditing}
            aria-invalid={!!foundedYearError}
            className={`h-10 rounded ${foundedYearError ? "border-destructive" : "border-gray-300"}`}
          />
          {foundedYearError && (
            <p className="text-sm text-destructive">{foundedYearError}</p>
          )}
        </div>

        {/* Industry multi-select (root / Level-1 industries). Limit by membership plan. */}
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-sm font-medium text-gray-700">
            Industry {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <p className="text-xs text-muted-foreground mb-1">
            Select up to {maxIndustrySelections} industries (no duplicates).
            Current plan: {maxIndustrySelections} allowed. Selected: {industryTypesList.length}.
            <span className="block mt-1">
              Saved industries are gray (non-removable). Newly added industries are blue (removable until you save).
            </span>
          </p>
        
          <Popover open={industryPopoverOpen} onOpenChange={setIndustryPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                disabled={!isEditing || atLimit}
                className="h-10 max-w-[240px] justify-between border-gray-300 rounded bg-white font-normal text-gray-700 hover:bg-gray-50"
              >
                <span className="truncate">
                  {atLimit ? `Maximum ${maxIndustrySelections} selected` : "Select industry (search & scroll)"}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] min-w-[200px] max-w-[320px] bg-white p-0 border border-gray-300 shadow-md"
              align="start"
            >
              <Command>
                <CommandInput placeholder="Search industry..." className="h-9 border-0 border-b focus:ring-0" />
                <CommandList className="max-h-[300px] overflow-y-auto">
                  <CommandEmpty>No industry found.</CommandEmpty>
                  <CommandGroup>
                    {industryOptions
                      .filter((c) => !industryTypesList.includes(c.id))
                      .map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => {
                            if (industryTypesList.includes(c.id) || industryTypesList.length >= maxIndustrySelections) return;
                            onFieldChange("industryTypes", [...industryTypesList, c.id]);
                            setIndustryPopoverOpen(false);
                          }}
                          disabled={industryTypesList.length >= maxIndustrySelections}
                          className="cursor-pointer"
                        >
                          {c.name}
                        </CommandItem>
                      ))}
                    {industryOptions.filter((c) => !industryTypesList.includes(c.id)).length === 0 && (
                      <div className="py-4 text-center text-sm text-muted-foreground">
                        {atLimit ? "Maximum reached" : "No more options"}
                      </div>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {industryTypesList.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-600 mb-1.5">Selected industries:</p>
              <div className="flex flex-wrap gap-2">
                {industryTypesList.map((id) => (
                  <span
                    key={id}
                    className={
                      savedIndustryTypesSet.has(id)
                        ? "inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800"
                        : "inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800"
                    }
                  >
                    {industryIdToName(id)}
                    {isEditing && !savedIndustryTypesSet.has(id) && (
                      <button
                        type="button"
                        onClick={() => {
                          onFieldChange(
                            "industryTypes",
                            industryTypesList.filter((x) => x !== id)
                          );
                        }}
                        className="rounded-full p-0.5 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-400"
                        aria-label={`Remove ${industryIdToName(id)} (unsaved)`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
          {errors.industryTypes && (
            <p className="text-sm text-destructive">{errors.industryTypes}</p>
          )}
        </div>
      </div>
    );
  }

  if (subSection === "contact-information") {
    const countryFieldBase = (ci.companyCountry ??
      ci.country ?? {
        label: "Country",
        value: "",
        editable: true,
        type: "select",
        required: true,
        placeholder: "Country",
      }) as ProfileField;
    const countryField: ProfileField = { ...countryFieldBase, required: true };
    const countryErr = errors.companyCountry ?? errors.country;
    const addressErr = errors.address ?? errors.companyAddress;
    const stateErr = errors.state ?? errors.companyState;
    const cityErr = errors.city ?? errors.companyCity;
    const pinErr = errors.postalCode ?? errors.companyPostalCode;
    const phoneErr = errors.companyPhone ?? errors.phone;
    const phoneInlineErr = phoneErr || (companyPhoneTooManyDigits ? "Maximum 10 digits allowed. Extra digits were not added." : undefined);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div id="profile-contact-country-anchor" className="space-y-1.5">
          <ProfileFieldRenderer
            field={countryField}
            fieldKey="companyCountry"
            isEditing={isEditing}
            onValueChange={(k, v) => onFieldChange(k, v)}
            error={countryErr}
            required
            hideRequiredAsteriskUnlessEditing
          />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="profile-contact-address" className="text-sm font-medium text-gray-700">
            Business Address{" "}
            {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <Input
            id="profile-contact-address"
            placeholder="Add address line"
            value={getStr(ci.address ?? ci.companyAddress)}
            onChange={(e) => onFieldChange("address", e.target.value)}
            disabled={!isEditing}
            aria-required
            aria-invalid={!!addressErr}
            className={`h-10 rounded ${addressErr ? "border-destructive" : "border-gray-300"}`}
          />
          {addressErr && <p className="text-sm text-destructive">{addressErr}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-contact-state" className="text-sm font-medium text-gray-700">
            State/Province{" "}
            {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <Input
            id="profile-contact-state"
            placeholder="Enter state/province"
            value={getStr(ci.state ?? ci.companyState)}
            onChange={(e) => onFieldChange("state", e.target.value)}
            disabled={!isEditing}
            aria-required
            aria-invalid={!!stateErr}
            className={`h-10 rounded ${stateErr ? "border-destructive" : "border-gray-300"}`}
          />
          {stateErr && <p className="text-sm text-destructive">{stateErr}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-contact-city" className="text-sm font-medium text-gray-700">
            City {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <Input
            id="profile-contact-city"
            placeholder="Enter city"
            value={getStr(ci.city ?? ci.companyCity)}
            onChange={(e) => onFieldChange("city", e.target.value)}
            disabled={!isEditing}
            aria-required
            aria-invalid={!!cityErr}
            className={`h-10 rounded ${cityErr ? "border-destructive" : "border-gray-300"}`}
          />
          {cityErr && <p className="text-sm text-destructive">{cityErr}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-contact-postalCode" className="text-sm font-medium text-gray-700">
            Pincode {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <Input
            id="profile-contact-postalCode"
            placeholder="Pincode"
            value={getStr(ci.postalCode ?? ci.companyPostalCode)}
            onChange={(e) => onFieldChange("postalCode", e.target.value)}
            disabled={!isEditing}
            aria-required
            aria-invalid={!!pinErr}
            className={`h-10 rounded ${pinErr ? "border-destructive" : "border-gray-300"}`}
          />
          {pinErr && <p className="text-sm text-destructive">{pinErr}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="profile-contact-companyPhone" className="text-sm font-medium text-gray-700">
            Mobile number{" "}
            {isEditing && <span className="text-red-600">*</span>}
          </Label>
          <Input
            id="profile-contact-companyPhone"
            placeholder="Phone number"
            value={getStr(ci.companyPhone).replace(/\D/g, "").slice(0, 10)}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "");
              setCompanyPhoneTooManyDigits(digits.length > 10);
              onFieldChange("companyPhone", digits.slice(0, 10));
            }}
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={!isEditing}
            aria-required
            aria-invalid={!!phoneInlineErr}
            className={`h-10 rounded ${phoneInlineErr ? "border-destructive" : "border-gray-300"}`}
          />
          {phoneInlineErr && <p className="text-sm text-destructive">{phoneInlineErr}</p>}
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-gray-700 block">
            Business Email{" "}
            {isEditing && <span className="text-red-600">*</span>}
          </span>
          <div
            id="profile-contact-companyEmail"
            tabIndex={isEditing ? 0 : -1}
            role="group"
            aria-required
            aria-invalid={!!errors.companyEmail}
            className={`h-10 px-3 py-2 border rounded bg-gray-50 text-gray-700 text-sm flex items-center outline-none focus:ring-0 focus-visible:ring-0 ${
              errors.companyEmail ? "border-destructive" : "border-gray-300"
            }`}
          >
            {getStr(ci.companyEmail) || "—"}
          </div>
          {errors.companyEmail && (
            <p className="text-sm text-destructive">{errors.companyEmail}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Read-only here. Add or update your business email in account settings if missing.
          </p>
        </div>
      </div>
    );
  }

  if (subSection === "company-registration") {
    const MAX_DOC_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
    const companyRegError = errors.companyRegistrationDetails;
    const gstDocField = (ci.gstDocumentUrl ?? {
      label: "GST Document",
      value: "",
      editable: true,
      type: "upload" as const,
      required: false,
    }) as ProfileField;
    const panDocField = (ci.panDocumentUrl ?? {
      label: "PAN Document",
      value: "",
      editable: true,
      type: "upload" as const,
      required: false,
    }) as ProfileField;
    const iecDocField = (ci.iecDocumentUrl ?? {
      label: "IEC Document",
      value: "",
      editable: true,
      type: "upload" as const,
      required: false,
    }) as ProfileField;
    return (
      <div className="space-y-4 sm:space-y-6">
        <p className="text-sm text-muted-foreground">
          Provide any one, two, or all three. For each, you can enter the number and/or upload the document.
        </p>
        {companyRegError && (
          <p className="text-sm text-destructive font-medium">{companyRegError}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 min-w-0">
          {/* GST */}
          {/* <div className="space-y-3 rounded-lg border border-gray-300 bg-gray-50/30 p-3 sm:p-4 min-w-0">
            <Label className="text-sm font-medium text-gray-700">GST</Label>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Number</Label>
              <Input
                placeholder="Enter GST Number"
                value={getStr(ci.gstNumber)}
                onChange={(e) => onFieldChange("gstNumber", e.target.value)}
                disabled={!isEditing}
                className="h-10 border-gray-300 rounded"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Document</Label>
              <CompanyRegistrationDocUpload
                readOnlyField={gstDocField}
                fieldKey="gstDocumentUrl"
                valueUrl={getStr(gstDocField)}
                isEditing={isEditing}
                onFieldChange={onFieldChange}
                onUpload={onUpload}
                maxFileSizeBytes={MAX_DOC_SIZE_BYTES}
                error={errors.gstDocumentUrl}
              />
            </div>
          </div> */}

<div className="space-y-3 rounded-lg border border-gray-300 bg-gray-50/30 p-3 sm:p-4 min-w-0">
  <Label className="text-sm font-medium text-gray-700">GST</Label>

  <div className="space-y-1.5">
    <Label className="text-xs text-gray-600">Number</Label>
    <Input
      placeholder="Enter GST Number"
      value={getStr(ci.gstNumber)}
      onChange={(e) => onFieldChange("gstNumber", sanitizeAlphaNumeric(e.target.value))}
      disabled={!isEditing}
      autoComplete="off"
      className="h-10 border-gray-300 rounded"
    />
  </div>

  <div className="space-y-1.5">
    <Label className="text-xs text-gray-600">Document</Label>

    <CompanyRegistrationDocUpload
      readOnlyField={gstDocField}
      fieldKey="gstDocumentUrl"
      valueUrl={getStr(gstDocField)}
      isEditing={isEditing}
      onFieldChange={onFieldChange}
      onUpload={onUpload}
      maxFileSizeBytes={MAX_DOC_SIZE_BYTES}
      error={errors.gstDocumentUrl}
    />

    {/* ✅ NEW: Preview Section
    {getStr(gstDocField) && (
      <div className="mt-2">
        <a
          href={getStr(gstDocField)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:underline"
        >
          Preview
        </a>

      </div>
    )} */}
  </div>
</div>
          {/* PAN */}
          <div className="space-y-3 rounded-lg border border-gray-300 bg-gray-50/30 p-3 sm:p-4 min-w-0">
            <Label className="text-sm font-medium text-gray-700">PAN</Label>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Number</Label>
              <Input
                placeholder="Enter PAN Number"
                value={getStr(ci.panNumber)}
                onChange={(e) => onFieldChange("panNumber", sanitizeAlphaNumeric(e.target.value))}
                disabled={!isEditing}
                autoComplete="off"
                className="h-10 border-gray-300 rounded"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Document</Label>
              <CompanyRegistrationDocUpload
                readOnlyField={panDocField}
                fieldKey="panDocumentUrl"
                valueUrl={getStr(panDocField)}
                isEditing={isEditing}
                onFieldChange={onFieldChange}
                onUpload={onUpload}
                maxFileSizeBytes={MAX_DOC_SIZE_BYTES}
                error={errors.panDocumentUrl}
              />
            </div>
          </div>
          {/* IEC */}
          <div className="space-y-3 rounded-lg border border-gray-300 bg-gray-50/30 p-3 sm:p-4 min-w-0">
            <Label className="text-sm font-medium text-gray-700">IEC</Label>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Number</Label>
              <Input
                placeholder="Enter IEC Number"
                value={getStr(ci.iecNumber)}
                onChange={(e) =>
                  onFieldChange("iecNumber", sanitizeAlphaNumeric(e.target.value))
                }
                disabled={!isEditing}
                autoComplete="off"
                className="h-10 border-gray-300 rounded"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600">Document</Label>
              <CompanyRegistrationDocUpload
                readOnlyField={iecDocField}
                fieldKey="iecDocumentUrl"
                valueUrl={getStr(iecDocField)}
                isEditing={isEditing}
                onFieldChange={onFieldChange}
                onUpload={onUpload}
                maxFileSizeBytes={MAX_DOC_SIZE_BYTES}
                error={errors.iecDocumentUrl}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (subSection === "about-company") {
    const desc =
      ci.description ??
      ({
        label: "About your company",
        value: "",
        editable: true,
        type: "text" as const,
        required: false,
        placeholder: "",
      } satisfies ProfileField);
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          This text appears on your public seller profile so buyers can learn about your business.
        </p>
        <div className="md:col-span-2">
          <ProfileFieldRenderer
            field={desc}
            fieldKey="description"
            isEditing={isEditing}
            onValueChange={onFieldChange}
            error={errors.description}
            hideRequiredAsteriskUnlessEditing
          />
        </div>
      </div>
    );
  }

  return null;
}
